// This file is exactly same as the CompoundInNHANESTimelineChart.tsx Needs to be fixed

import React, { useEffect, useRef, useState } from 'react';
import { ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { BaseTimelineChartProps } from '../../../types/reports';
import { truncateToTwoDecimals } from '../../../utils/reports';
import { Box } from '@mui/material';
import { ABOVE_RANGE, BELOW_RANGE, INCONCLUSIVE, NOT_DETECTED } from '../../../constants/report';
import { ResultTimelineCompoundTooltip } from '../elements/ResultTimelineCompoundTooltip';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

interface CustomDoubleLabelProps {
  viewBox: {
    x: number;
    y: number;
    width: number;
  };
  valueLeft: string;
  valueRight: string;
}

const CHART_HEIGHT = 400;

const calculateTopRange = (value: number, higherTopValue: number) => {
  if (value < 0.5) return 0.5;
  if (value < 1) return 1;
  if (value >= 1 && value < 5) return 5;
  if (value >= 5 && value < 10) return 10;
  if (value >= 10 && value < 20) return 20;
  if (value >= 20 && value < 30) return 30;
  if (value >= 30 && value < 40) return 40;

  return higherTopValue;
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const lineData = payload.find((item: { dataKey: string }) => item.dataKey === 'compoundPercent');

    if (lineData) {
      const data = lineData.payload;

      return <ResultTimelineCompoundTooltip compoundData={data} />;
    }
  }
  return null;
};

const CompoundNotInNHANESTimelineChart: React.FC<BaseTimelineChartProps> = ({
  allResults,
  selectedCompound,
  compoundMetaData,
}) => {
  const { theme } = useThemeContext();
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setChartWidth(chartRef.current.offsetWidth || 0);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const ICONS = {
    INCONCLUSIVE: theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.images.inconclusive,
    NOT_DETECTED: theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.images.notDetected,
    ABOVE_RANGE: theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.images.aboveRange,
    BELOW_RANGE: theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.images.belowRange,
    BEYOND_REPORTABLE_RANGE:
      theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.images.beyondReportableRange,
  };

  const CustomDoubleLabel = ({ viewBox, valueLeft, valueRight }: CustomDoubleLabelProps) => {
    const { x, y, width } = viewBox;
    return (
      <>
        <text
          x={x + 45} // Move text right to ensure visibility
          y={y + 4}
          textAnchor="end"
          style={{
            fill: theme.custom.report.timeline.compoundNotInNHANESTimelineChart.customDoubleLabel.left.color,
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {valueLeft}
        </text>

        <text
          x={x + width - 50}
          y={y - 10}
          textAnchor="end"
          style={{
            fill: theme.custom.report.timeline.compoundNotInNHANESTimelineChart.customDoubleLabel.left.color,
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {valueRight}
        </text>
      </>
    );
  };

  const loq =
    selectedCompound && compoundMetaData?.[selectedCompound]?.loq ? compoundMetaData[selectedCompound].loq : '---';

  const detectionFrequency =
    selectedCompound && compoundMetaData?.[selectedCompound]?.detection_freq
      ? compoundMetaData[selectedCompound].detection_freq
      : '---';

  // Compute `lowerReportableValue` and `topRange` dynamically before rendering
  let maxValue = -Infinity;
  let minValue = Infinity;
  let highestTopRange = 0;
  let highestHigherReportableValue = 0;
  let lowestLowerReportableValue = Infinity;

  allResults.forEach((report) => {
    const selectedCompoundData = report.compoundDataWithDdfv.find(
      (compound: { ddfv: { testNameAbbr: string } }) => compound.ddfv.testNameAbbr === selectedCompound,
    );

    if (selectedCompoundData?.ddfv.valueAsFloat !== undefined) {
      const value = selectedCompoundData.ddfv.valueAsFloat;
      if (value !== null && !isNaN(value)) {
        maxValue = Math.max(maxValue, value);
        minValue = Math.min(minValue, value);
      }
    }

    if (
      selectedCompoundData?.ddfv.higherReportableValue !== undefined &&
      selectedCompoundData?.ddfv.higherReportableValue !== '-'
    ) {
      highestHigherReportableValue = Math.max(
        highestHigherReportableValue,
        selectedCompoundData.ddfv.higherReportableValue,
      );
    }

    if (
      selectedCompoundData?.ddfv.lowerReportableValue !== undefined &&
      selectedCompoundData?.ddfv.lowerReportableValue !== '-'
    ) {
      lowestLowerReportableValue = Math.min(lowestLowerReportableValue, selectedCompoundData.ddfv.lowerReportableValue);
    } else {
      // If no valid lowerReportableValue is found, set a default fallback to selected compound LOQ
      lowestLowerReportableValue = Number(loq);
    }

    // Calculate top range for this specific data point and retain the highest
    const currentTopRange = calculateTopRange(maxValue, highestHigherReportableValue);
    highestTopRange = Math.max(highestTopRange, currentTopRange);
  });

  if (highestHigherReportableValue === 0 || highestHigherReportableValue === -Infinity) {
    // If no valid higherReportableValue was found, fallback to maxValue (if available) or 50
    highestHigherReportableValue = maxValue > 0 ? maxValue : 50;
  }

  const lowerReportableValue =
    lowestLowerReportableValue === Infinity ? 0 : truncateToTwoDecimals(lowestLowerReportableValue);
  const topRange = highestTopRange;

  let chartData = allResults
    .map((report) => {
      const selectedCompoundData = report.compoundDataWithDdfv.find(
        (compound: { ddfv: { testNameAbbr: string } }) => compound.ddfv.testNameAbbr === selectedCompound,
      );

      if (!selectedCompoundData || selectedCompoundData.ddfv.label.trim().toLowerCase().includes('unknown')) {
        return null;
      }

      // Extract the valueAsFloat if it exists; otherwise, set it to null
      let compoundValueFloat = selectedCompoundData?.ddfv.valueAsFloat ?? null;

      let compoundValueStr = selectedCompoundData?.value ?? '';
      let compoundLabel = selectedCompoundData?.ddfv.label;
      let compoundDisplayValue = '';

      if (compoundLabel.toLowerCase().trim().includes(INCONCLUSIVE.toLowerCase())) {
        compoundValueFloat = 0;
        compoundDisplayValue = 'Inconclusive';
      } else if (compoundLabel.toLowerCase().trim().includes(ABOVE_RANGE.toLowerCase())) {
        compoundValueFloat = 0;
        compoundDisplayValue = 'Above Reportable Range';
      } else if (compoundLabel.toLowerCase().trim().includes(BELOW_RANGE.toLowerCase())) {
        compoundValueFloat = 0;
        compoundDisplayValue = 'Below Reportable Range';
      } else if (compoundLabel.toLowerCase().trim().replace(/\s+/g, ' ').includes(NOT_DETECTED.toLowerCase())) {
        compoundValueFloat = 0;
        compoundDisplayValue = 'Not Detected';
      } else {
        compoundDisplayValue = compoundValueStr;
      }

      return {
        date: new Date(report.report_date).toLocaleDateString('en-US', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
        }),
        compoundPercent: compoundValueFloat,
        compoundDisplayValue,
        compoundLabel,
        loq,
        detectionFrequency,
      };
    })
    .filter(Boolean);

  const referenceLines = [
    { y: lowerReportableValue, leftLabel: `${lowerReportableValue} ng/mL`, rightLabel: '' },
    { y: topRange, leftLabel: `${topRange} ng/mL`, rightLabel: '' },
  ];

  const upperBound = calculateTopRange(maxValue, highestHigherReportableValue);

  const getIconForLabel = (label: string): string | null => {
    if (!label) return null;
    const normalizedLabel = label.toLowerCase().trim().replace(/\s+/g, ' ');

    if (normalizedLabel.includes(INCONCLUSIVE.toLowerCase())) return ICONS.INCONCLUSIVE;
    if (normalizedLabel.includes(NOT_DETECTED.toLowerCase())) return ICONS.NOT_DETECTED;
    if (normalizedLabel.includes(ABOVE_RANGE.toLowerCase())) return ICONS.ABOVE_RANGE;
    if (normalizedLabel.includes(BELOW_RANGE.toLowerCase())) return ICONS.BELOW_RANGE;

    return null;
  };

  const renderCustomDot = ({ cx, cy, payload, isActive = false }: any) => {
    const icon = getIconForLabel(payload.compoundLabel);
    const isAboveRange = payload.compoundLabel.toLowerCase().includes(ABOVE_RANGE.toLowerCase());

    if (isAboveRange && icon) {
      return (
        <>
          <line
            x1={cx}
            y1={20}
            x2={cx}
            y2={CHART_HEIGHT - 50}
            stroke={
              theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.line.dot.isAboveRange.line
                .stroke.color
            }
            strokeWidth={2}
            strokeDasharray="4 4"
          />
          <image href={icon} x={cx - 12} y={5} width="24" height="24" />
        </>
      );
    }

    if (icon) {
      return <image href={icon} x={cx - 10} y={CHART_HEIGHT - 70} width="20" height="20" />;
    }

    // Default dot rendering for non-special cases
    return (
      <>
        <line
          x1={cx}
          y1={cy}
          x2={cx}
          y2={CHART_HEIGHT - 40}
          stroke={
            theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.line.dot.default.line.stroke
              .color
          }
        />
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={
            theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.line.dot.default.circle.fill
              .color
          }
          stroke={
            isActive
              ? theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.line.activeDot.default
                  .circle.stroke.color
              : theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.line.dot.default.circle
                  .stroke.color
          }
          strokeWidth={1}
        />
      </>
    );
  };

  const renderCompoundTimelineGraph = () => {
    return (
      <div
        ref={chartRef}
        style={{
          position: 'relative',
          width: '100%',
          height: CHART_HEIGHT,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 20, right: 50, left: 30, bottom: 20 }}>
            <XAxis
              dataKey="date"
              tick={{
                fontSize: 12,
                fill: theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.xAxis.tick.color,
                fontWeight: 400,
              }}
              axisLine={{
                stroke:
                  theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.xAxis.axisLine.color,
              }}
              tickLine={{
                stroke:
                  theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.xAxis.tickLine.color,
              }}
              padding={{ left: 80, right: 80 }}
            />
            <YAxis
              hide
              domain={[-1, upperBound]}
              tick={{
                fontSize: 12,
                fill: theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.yAxis.tick.color,
              }}
              axisLine={{
                stroke:
                  theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.yAxis.axisLine.color,
              }}
              tickLine={{
                stroke:
                  theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.yAxis.tickLine.color,
              }}
            />
            <Tooltip
              cursor={false}
              content={({ active, payload }) => <CustomTooltip active={active} payload={payload} />}
            />

            {referenceLines.map((line, index) => (
              <ReferenceLine
                key={index}
                y={line.y}
                stroke={
                  theme.custom.report.timeline.compoundNotInNHANESTimelineChart.composedChart.referenceLines.stroke
                    .color
                }
                strokeWidth={2}
                label={
                  <CustomDoubleLabel
                    viewBox={{
                      x: -10,
                      y: (CHART_HEIGHT * (100 - line.y)) / 100,
                      width: chartWidth,
                    }}
                    valueLeft={line.leftLabel}
                    valueRight={line.rightLabel}
                  />
                }
                ifOverflow="visible"
                clipPath="inset(0px 0px 0px 50px)"
              />
            ))}

            <Line
              type="linear"
              dataKey="compoundPercent"
              stroke="none"
              activeDot={(props: any) => renderCustomDot({ ...props, isActive: true })}
              dot={renderCustomDot}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return <Box>{renderCompoundTimelineGraph()}</Box>;
};

export default CompoundNotInNHANESTimelineChart;
